import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Form, Input, Select, message } from "antd";
import {
  fundExceptionsApi,
  type FundExceptionDetail,
  type RecommendedAction,
} from "../../api/fund-exceptions.api";
import { DraggableModal } from "../../components/DraggableModal";
import { startAction } from "../../observability/otel";

interface ResolveFormValues {
  action: RecommendedAction;
  reason: string;
}

export interface FundExceptionResolveModalProps {
  open: boolean;
  exception: FundExceptionDetail;
  onClose: () => void;
  onResolved: () => void | Promise<void>;
}

const ACTION_OPTIONS: Array<{ value: RecommendedAction; label: string }> = [
  { value: "GRANT_ENTITLEMENT", label: "补开课时" },
  { value: "REFUND", label: "原路退回" },
  { value: "CORRECT_AMOUNT", label: "金额纠正" },
  { value: "REVERSE_ENTITLEMENT", label: "课时冲正" },
  { value: "RETRY", label: "重试" },
  { value: "CLOSE", label: "关闭异常" },
];

/**
 * 资金异常处理对话框：动作默认取推荐值，业务原因必填并随单次意图携带幂等键。
 */
export function FundExceptionResolveModal({
  open,
  exception,
  onClose,
  onResolved,
}: FundExceptionResolveModalProps) {
  const [form] = Form.useForm<ResolveFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const idempotencyKeyRef = useRef("");

  useEffect(() => {
    if (!open) return;
    idempotencyKeyRef.current = crypto.randomUUID();
    setServerError(null);
    form.setFieldsValue({ action: exception.recommendedAction, reason: "" });
  }, [open, exception.recommendedAction, form]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    const values = await form.validateFields().catch(() => null);
    if (!values) return;

    setSubmitting(true);
    setServerError(null);
    const span = startAction("action: payment.resolve", {
      pageCode: "ADM-14",
      action: "payment.resolve",
    });
    try {
      await fundExceptionsApi.resolveFundException(exception.exceptionId, {
        action: values.action,
        reason: values.reason.trim(),
        idempotencyKey: idempotencyKeyRef.current,
      });
      message.success("资金异常处理已提交");
      await onResolved();
      onClose();
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : "资金异常处理失败",
      );
    } finally {
      span.end();
      setSubmitting(false);
    }
  }, [exception.exceptionId, form, onClose, onResolved, submitting]);

  return (
    <DraggableModal
      open={open}
      title={`处理资金异常 · ${exception.exceptionNo}`}
      okText="确认处理"
      cancelText="取消"
      confirmLoading={submitting}
      onOk={() => void handleSubmit()}
      onCancel={() => {
        if (!submitting) onClose();
      }}
      destroyOnHidden
      width={520}
      data-testid="fund-exception-resolve-modal"
    >
      {serverError && (
        <Alert
          type="error"
          showIcon
          message={serverError}
          style={{ marginBottom: 12 }}
          data-testid="fund-exception-resolve-error"
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{ action: exception.recommendedAction }}
      >
        <Form.Item
          name="action"
          label="处理动作"
          rules={[{ required: true, message: "请选择处理动作" }]}
        >
          <Select
            options={ACTION_OPTIONS}
            data-testid="fund-exception-action-select"
          />
        </Form.Item>
        <Form.Item
          name="reason"
          label="业务原因"
          validateFirst
          rules={[
            { required: true, message: "请填写业务原因" },
            {
              validator: (_, value: string | undefined) =>
                value?.trim()
                  ? Promise.resolve()
                  : Promise.reject(new Error("请填写业务原因")),
            },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={512}
            placeholder="请输入处理原因，将写入审计记录"
            data-testid="fund-exception-reason-input"
          />
        </Form.Item>
      </Form>
    </DraggableModal>
  );
}
